import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Introduction from './Components/Introduction';
import Starter from './Components/Starter';
import MenuBar from './Components/MenuBar';
import styles from './Styles/Main.module.css';
import Grid from './Components/Grid';
import Trending from './Components/Trending';
import Winter from './Components/Winter';
import WinterMobile from './Components/WinterMobile';
import Spring from './Components/Spring';
import SpringMobile from './Components/SpringMobile';
import Summer from './Components/Summer';
import SummerMobile from './Components/SummerMobile';
import Autum from './Components/Autum';
import AutumnMobile from './Components/AutumnMobile';
import Contact from './Components/Contact';
import { useIsMobile } from './Components/useIsMobile';


const APP_STATES = {
  LOADING: 'loading',
  INTRO: 'intro',
  CONTENT: 'content',
};

export default function Main() {
  const [appState, setAppState] = useState(APP_STATES.LOADING);
  const isMobile = useIsMobile();
  
  // Refs for section navigation
  const refs = useMemo(() => ({
  B2Home: React.createRef(),
  whyShiviraTravel: React.createRef(),
  gridRef: React.createRef(),
  TrendRef: React.createRef(),
  winterRef: React.createRef(),
  springRef: React.createRef(),
  summer: React.createRef(),
  autumnRef: React.createRef(),
  contactRef: React.createRef(),
}), []);

  const hasCheckedStorage = useRef(false);

  // Check session storage to determine if the intro should be shown
  useEffect(() => {
  if (hasCheckedStorage.current) return; // ensure it runs only once per component mount
  hasCheckedStorage.current = true;

  try {
    const introShown = sessionStorage.getItem('introShown');
    if (introShown === 'true') {
      setAppState(APP_STATES.CONTENT);
    } else {
      setAppState(APP_STATES.INTRO);
      sessionStorage.setItem('introShown', 'true');
    }
  } catch (error) {
    console.warn('Session storage unavailable, showing intro by default:', error);
    setAppState(APP_STATES.INTRO);
  }
}, []);


  const handleIntroComplete = useCallback(() => {
    setAppState(APP_STATES.CONTENT);
  }, []);

  const Components = useMemo(() => ({
    Winter: isMobile ? WinterMobile : Winter,
    Spring: isMobile ? SpringMobile : Spring,
    Summer: isMobile ? SummerMobile : Summer,
    Autumn: isMobile ? AutumnMobile : Autum,
  }), [isMobile]);

  
  const content = useMemo(() => (
    <div
      className={styles.content}
      role="main"
      style={{
        opacity: appState === APP_STATES.CONTENT ? 1 : 0,
        transition: 'opacity 0.2s ease-in',
      }}
    >
      <MenuBar
        B2Home={refs.B2Home}
        whyShiviraTravel={refs.whyShiviraTravel}
        contactRef={refs.contactRef}
        autumnRef={refs.autumnRef}
        summerPageRef={refs.summer}
        gridRef={refs.gridRef}
        TrendRef={refs.TrendRef}
        winterRef={refs.winterRef}
        springRef={refs.springRef}
      />
      <Starter B2Home={refs.B2Home} whyShiviraTravel={refs.whyShiviraTravel} />
      <Grid ref={refs.gridRef} />
      <Trending ref={refs.TrendRef} />
      <Components.Winter ref={refs.winterRef} />
      <Components.Spring summer={refs.summer} ref={refs.springRef} />
      <Components.Summer ref={isMobile ? refs.summer : null} />
      <Components.Autumn ref={refs.autumnRef} />
      <Contact ref={refs.contactRef} />
    </div>
  ), [appState, isMobile, refs]);

  if (appState === APP_STATES.LOADING) {
    return <div className={styles.loading} aria-label="Loading" />;
  }

  return (
    <div className={styles.main}>
      {appState === APP_STATES.INTRO && (
        <Introduction onComplete={handleIntroComplete} aria-label="Introduction animation" />
      )}
      {content}
    </div>
  );
}